import { create } from "zustand";
import { registerOrgScopedReset } from '@/lib/org-scope';
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { sanitizeArtifactLabel, sanitizeArtifactMarkdown } from "@/services/multi-agent/artifactDisplay";

/** Remove ``` fenced code blocks from a string (plain scan, no regex). */
function stripCodeFences(value: string): string {
  let out = "";
  let i = 0;
  while (i < value.length) {
    if (value[i] === "`" && value[i + 1] === "`" && value[i + 2] === "`") {
      const close = value.indexOf("```", i + 3);
      if (close < 0) break; // unterminated fence — drop the rest
      i = close + 3;
      continue;
    }
    out += value[i];
    i++;
  }
  return out;
}

/**
 * Turn a raw run intent into a clean question for display. A refactor send embeds
 * the whole class source as `[Refactor target: Name (Type) · id=…]```apex …``` text`
 * for the backend — the chat bubble should show only `[File: Name] text`, never the
 * code dump. Also handles the `[Context from …]` MCP prefix. No regex.
 */
export function cleanDisplayIntent(raw: string | null | undefined): string {
  let s = raw || "";
  let prefix = "";
  if (s.startsWith("[Refactor target:")) {
    const close = s.indexOf("]");
    if (close >= 0) {
      const inner = s.slice("[Refactor target:".length, close).trim();
      let nameEnd = inner.length;
      const paren = inner.indexOf(" (");
      const dot = inner.indexOf(" ·"); // " ·"
      if (paren >= 0) nameEnd = Math.min(nameEnd, paren);
      if (dot >= 0) nameEnd = Math.min(nameEnd, dot);
      const name = inner.slice(0, nameEnd).trim();
      prefix = name ? `[File: ${name}] ` : "";
      s = s.slice(close + 1);
    }
  } else if (s.startsWith("[Context from ")) {
    const close = s.indexOf("]");
    if (close >= 0) {
      prefix = `${s.slice(0, close + 1)} `;
      s = s.slice(close + 1);
    }
  }
  const cleaned = stripCodeFences(s).trim();
  return (prefix + cleaned).trim() || raw || "";
}

export type NodeStatus =
  | "planned"
  | "ready"
  | "running"
  | "tool_calling"
  | "done"
  | "failed"
  | "skipped";

export interface AgentNodeData {
  [key: string]: unknown;
  id: string;
  role: string;
  task: string;
  status: NodeStatus;
  toolInFlight?: string;
  output?: { summary?: string };
  isSupervisor?: boolean;
}

export interface PlanEdge {
  from: string;
  to: string;
  active?: boolean;
}

/** Lightweight attachment shape carried on a sent user message so the chat
 *  bubble can render inline thumbnails / chips (image preview, file icon+name).
 *  Holds only what the bubble needs — the in-memory client thumbnail, not the
 *  base64 payload — so it stays small and never bloats persisted state. */
export interface ChatMsgAttachment {
  id: string;
  name: string;
  kind: string;                // image | pdf | audio | url | text | code
  thumbnailDataUrl?: string;   // images only — in-memory client preview
}

export interface ChatMessage {
  id: string;
  role: "user" | "assistant" | "system";
  text: string;
  reasoning?: string;          // muted italic — Anthropic extended thinking
  streaming?: boolean;
  attachments?: ChatMsgAttachment[]; // sent user message → inline bubble thumbnails (#11)
}

export interface TodoItem {
  id: string;
  label: string;
  status: "queued" | "running" | "done" | "failed";
}

export interface MultiAgentState {
  runId: string | null;
  status:
    | "idle"
    | "starting"
    | "planning"
    | "awaiting_approval"
    | "running"
    | "done"
    | "failed"
    | "cancelled";
  lane: string | null;
  // True when the current view is a historical run loaded for reading.
  // Disables the input box, approve, and stop — clicking a past run from
  // the history list flips this on; "New run" flips it off and clears.
  readOnly: boolean;
  // Run timing (ms epoch) anchored in the store so the elapsed timer survives
  // tab-switches / re-mounts and captures the WHOLE run, not just on-page time.
  runStartedAt: number | null;
  runFinishedAt: number | null;

  chat: ChatMessage[];
  todo: TodoItem[];

  nodes: Record<string, AgentNodeData>;
  edges: PlanEdge[];

  // streaming buffers keyed by node_id
  reasoningBuffer: Record<string, string>;
  textBuffer: Record<string, string>;
  finalText: string;

  // partial findings streamed from workers as they finish
  partialFindings: Array<{ nodeId: string; role: string; summary: string }>;

  // learning lookback stats (memory_loaded event at planner start)
  memoryLoaded: { similarRuns: number; knownFacts: number; recentGaps: number } | null;

  // Prior turns/artifacts loaded from the current conversation session.
  // Backend emits this when the planner stitched follow-up context.
  sessionContext: {
    turnCount: number;
    artifactCount: number;
    badToolCount: number;
    compressed: boolean;
    compressedChars?: number;
    keptTurns?: number | null;
  } | null;

  // promotion totals at finalize
  promotionTotals: { factCount: number; artifactCount: number } | null;

  // live cost meter
  cost: { usedUsd: number; maxUsd: number; tokensIn: number; tokensOut: number; toolCalls: number } | null;

  // final synthesized markdown (separate from streaming buffer so we can
  // render a polished result card)
  finalMarkdown: string;
  finalArtifactId: string | null;

  // Conversation-grounded follow-up suggestions from the backend
  // (`followup_suggestions` SSE event / hydrated summary). When present the UI
  // shows these instead of the static lane templates.
  followUps: Array<{ label: string; hint: string; question: string }>;

  // recent runs history (for resume-on-mount)
  history: Array<{
    id: string;
    session_id?: string | null;
    user_intent: string;
    lane: string | null;
    status: string;
    started_at: string;
    finished_at: string | null;
    node_count: number;
    cost_usd: number;
    tokens_total: number;
  }>;

  // user feedback recorded
  feedbackRating: number | null;

  // auto-fix proposals (P12)
  proposedFixes: Array<{
    kind: string;
    // New code_patch contract fields (iter 14+). `target` retained as a
    // fallback for legacy proposals.
    file?: string;
    component_name?: string;
    metadata_type?: string;
    validation_status?: string;
    target?: string;
    risk: string;
    summary: string;
  }>;

  // verifier verdict — score 0–10, gap list, no_evidence flag (audit lane
  // had zero org tool calls). Surfaced as a tile + a safety banner so
  // the user can see HOW grounded the answer is.
  verifier: {
    ok: boolean;
    score: number;
    gaps: string[];
    noEvidence: boolean;
    replanCount: number;
  } | null;

  // Synth produced output that violated the lane contract (e.g. missing
  // diff fence on apex_change). Surfaced as a banner so the user knows
  // the answer below may be malformed even if it streamed cleanly.
  contractViolation: { lane: string; violations: string[] } | null;

  // Blocked-state gate (dev-agent parity): when the org's Salesforce metadata /
  // RAG code index isn't ready, the run is short-circuited with a message and
  // this carries the optional retrigger action (Start/Retry indexing) so the
  // answer column can render the button. null = no action (in_progress / metadata).
  blockedAction: { kind: string; label: string; endpoint: string; enabled?: boolean } | null;

  // auto_fix.propose failed — Apply path won't be available for this
  // run. Surfaced so the user knows it's not a UI bug.
  autoFixFailed: { reason: string } | null;

  // Thread of prior Q&A turns in the current chat session. Refine
  // pushes the current run into this array before starting the next,
  // so previous answers stay visible above the active run instead of
  // each follow-up wiping the screen. Cleared by "New run".
  thread: Array<{
    runId: string;
    intent: string;
    attachments?: ChatMsgAttachment[]; // sent-message thumbnails for past turns (#11)
    finalMarkdown: string;
    lane: string | null;
    // Snapshot of what the agent DID this turn (timeline + reasoning
    // tail). Lets the user scroll back through prior turns and remember
    // what the agent looked at, not just what it answered.
    activity: Array<{
      id: number;
      kind: string;
      text: string;
      tone: "info" | "ok" | "warn" | "err";
      tool?: string;
      argsSummary?: string;
      durationMs?: number;
    }>;
    reasoning: string;
    artifactIds: string[];
  }>;
  // The conversation session_id. Set when the first run in a chat
  // starts; persisted across follow-ups so the backend planner can
  // pull prior turns. Cleared by clearThread().
  sessionId: string | null;

  // signal that the planner couldn't produce a real plan and fell back to
  // a synthesizer-only answer — surfaced as a banner so the user knows.
  plannerFallback: { reason: string; rawPreview: string } | null;

  // signal that the planner reused a past run's plan (high similarity match)
  planReused: { fromRunId: string; similarity: number; lane: string } | null;

  // Artifacts produced during the run — each tool output that was promoted
  // to the artifact store fires an artifact_created event. The Result
  // column renders these as copyable cards so the user can pull individual
  // pieces of evidence without scrolling through the whole markdown.
  artifacts: Array<{
    id: string;
    type: string;
    name: string;
    toolName: string;
    byteSize: number;
    createdAt: number;
    // The run that PRODUCED this artifact. Lets each Q→A turn render its OWN
    // results/evidence/diff/code instead of the whole session's artifacts piling
    // up at the end of the chat. The Workspace still shows the accumulated union.
    runId?: string;
    // Typed-metadata blob for uploaded attachments (tool_name='intake'):
    // { source_kind, mime, render, title?, final_url?, page_count?, line_count?,
    //   transcript_chars? }. Present only for intake artifacts; null otherwise.
    intake?: Record<string, unknown> | null;
  }>;

  // node selected in the graph (clicked) — opens the per-agent detail drawer
  selectedNodeId: string | null;

  // Live SSE connection state — surfaced in the header pill so the user
  // knows if the stream is healthy.
  sseStatus: "idle" | "connecting" | "connected" | "disconnected" | "reconnecting";

  // Salesforce connection health for the chat pill (dev-agent parity).
  // Optimistic-true; flipped false by an auth_required / sf_token_unauthorized
  // SSE event. The pill lets the user manually reconnect (REFRESH_SALESFORCE_TOKEN).
  sfConnected: boolean;

  // Chronological activity log feeding the "Steps" column — every tool
  // call, validation, replan, approval, etc. appears here in arrival order.
  activityLog: Array<{
    id: number;                                     // _event_id
    ts: number;                                     // emitted_at_ms
    kind: string;                                   // event_type
    nodeId?: string | null;
    text: string;                                   // human-readable summary
    tone: "info" | "ok" | "warn" | "err";
    // Tool-call extras (populated for tool_call_started/completed). Lets
    // the Steps column render a proper tool pill like the dev-agent chat
    // instead of just a text line.
    tool?: string;
    argsSummary?: string;
    durationMs?: number;
    ok?: boolean;
    resultSummary?: string;
  }>;

  // === actions ===
  startRun: (runId: string, intent: string, attachments?: ChatMsgAttachment[]) => void;
  setStatus: (s: MultiAgentState["status"]) => void;
  setLane: (lane: string) => void;
  setBlockedAction: (action: MultiAgentState["blockedAction"]) => void;
  appendUserMessage: (text: string) => void;
  appendReasoning: (nodeId: string, text: string) => void;
  endReasoning: (nodeId: string) => void;
  appendText: (nodeId: string, text: string) => void;
  endText: (nodeId: string) => void;
  setPlan: (nodes: AgentNodeData[], edges: PlanEdge[], todo: TodoItem[]) => void;
  setNodeStatus: (nodeId: string, status: NodeStatus, toolInFlight?: string) => void;
  setNodeOutput: (nodeId: string, output: AgentNodeData["output"]) => void;
  activateEdge: (from: string, to: string) => void;
  approvePlan: () => void;
  addPartialFinding: (nodeId: string, role: string, summary: string) => void;
  setMemoryLoaded: (m: NonNullable<MultiAgentState["memoryLoaded"]>) => void;
  setSessionContext: (m: NonNullable<MultiAgentState["sessionContext"]>) => void;
  setPromotionTotals: (t: NonNullable<MultiAgentState["promotionTotals"]>) => void;
  setCost: (c: NonNullable<MultiAgentState["cost"]>) => void;
  setFinalArtifact: (artifactId: string | null) => void;
  setFollowUps: (f: MultiAgentState["followUps"]) => void;
  setHistory: (h: MultiAgentState["history"]) => void;
  setFeedback: (rating: number) => void;
  setProposedFixes: (fixes: MultiAgentState["proposedFixes"]) => void;
  setVerifier: (v: NonNullable<MultiAgentState["verifier"]>) => void;
  setContractViolation: (v: NonNullable<MultiAgentState["contractViolation"]>) => void;
  setAutoFixFailed: (v: NonNullable<MultiAgentState["autoFixFailed"]>) => void;
  // Snapshot the current run into the thread (called before refine).
  pushTurnToThread: () => void;
  // Clear the thread (called by "New run").
  clearThread: () => void;
  // Internal: mirror persistent fields to localStorage.
  _persistMA: () => void;
  setPlannerFallback: (f: MultiAgentState["plannerFallback"]) => void;
  setPlanReused: (p: MultiAgentState["planReused"]) => void;
  selectNode: (nodeId: string | null) => void;
  appendActivity: (entry: MultiAgentState["activityLog"][number]) => void;
  setSseStatus: (s: "idle" | "connecting" | "connected" | "disconnected" | "reconnecting") => void;
  setSfConnected: (v: boolean) => void;
  addArtifact: (a: MultiAgentState["artifacts"][number]) => void;
  removeArtifact: (id: string) => void;
  markCancelled: () => void;
  // Clean up after a normal terminal event (run_completed / run_failed
  // / budget_exceeded). Flips any still-in-flight node to a terminal
  // status and clears tool spinners so the UI doesn't keep spinning
  // after the answer is already shown.
  finalizeRun: (status: "done" | "failed") => void;
  hydrateFromDetail: (detail: {
    run: { id: string; session_id?: string | null; user_intent: string; lane: string | null; status: string; started_at?: string | null; finished_at?: string | null };
    session_id?: string | null;
    plan: { tasks: Array<{ id: string; role: string; task: string }>; edges?: [string, string][] } | null;
    todo: Array<{ id: string; label: string; status: string }>;
    nodes: Array<{ id: string; role: string; task: string; status: string; isSupervisor?: boolean; toolInFlight?: string | null }>;
    edges: [string, string][];
    chat: Array<{ role: "user" | "assistant"; text?: string; reasoning?: string }>;
    partial_findings: Array<{ nodeId: string; role: string; summary: string }>;
    proposed_fixes: Array<{ kind: string; target: string; risk: string; summary: string }>;
    final_markdown: string;
    final_artifact_id: string | null;
    follow_ups?: Array<{ label: string; hint: string; question: string }>;
    cost: { usedUsd: number; maxUsd: number; tokensIn: number; tokensOut: number; toolCalls: number } | null;
    memory_loaded: { similarRuns: number; knownFacts: number; recentGaps: number } | null;
    session_context: MultiAgentState["sessionContext"];
    session_runs?: Array<{
      runId: string;
      intent: string;
      finalMarkdown: string;
      lane: string | null;
      status: string;
      startedAt: string | null;
      finishedAt: string | null;
      artifacts?: Array<{ id: string; type: string; name: string; tool_name: string; metadata: Record<string, unknown> | null; created_at: string | null }>;
    }>;
    promotion_totals: { factCount: number; artifactCount: number } | null;
    artifacts?: Array<{ id: string; type: string; name: string; tool_name: string; metadata: Record<string, unknown> | null; created_at: string | null }>;
  }, options?: { preserveSession?: boolean; viewOnly?: boolean }) => void;
  reset: () => void;
}

const _initial: Pick<
  MultiAgentState,
  "runId" | "status" | "lane" | "chat" | "todo" | "nodes" | "edges"
  | "reasoningBuffer" | "textBuffer" | "finalText" | "partialFindings"
  | "memoryLoaded" | "sessionContext" | "promotionTotals" | "cost" | "finalMarkdown" | "finalArtifactId" | "followUps" | "feedbackRating" | "proposedFixes" | "verifier" | "contractViolation" | "blockedAction" | "autoFixFailed" | "thread" | "sessionId" | "plannerFallback" | "planReused" | "selectedNodeId" | "activityLog" | "sseStatus" | "sfConnected" | "artifacts" | "readOnly"
  | "runStartedAt" | "runFinishedAt"
> = {
  runId: null,
  status: "idle",
  lane: null,
  chat: [],
  todo: [],
  nodes: {},
  edges: [],
  reasoningBuffer: {},
  textBuffer: {},
  finalText: "",
  partialFindings: [],
  memoryLoaded: null,
  sessionContext: null,
  promotionTotals: null,
  cost: null,
  finalMarkdown: "",
  finalArtifactId: null,
  followUps: [],
  feedbackRating: null,
  proposedFixes: [],
  verifier: null,
  contractViolation: null,
  blockedAction: null,
  autoFixFailed: null,
  thread: [],
  sessionId: null,
  plannerFallback: null,
  planReused: null,
  selectedNodeId: null,
  activityLog: [],
  sseStatus: "idle",
  sfConnected: true,
  artifacts: [],
  readOnly: false,
  runStartedAt: null,
  runFinishedAt: null,
};

// Local persistence — scoped to user + org. Stores only runId + sessionId;
// thread body is restored from GET /detail (session_runs) on hydrate.
const LEGACY_STORAGE_KEY = "ma:session";

export type PersistedMultiAgentSession = {
  runId: string | null;
  sessionId: string | null;
};

function resolveMultiAgentTenant(): { userId: string; orgId: string } | null {
  const state = useAuthStoreV1.getState();
  const userId = state.user?.id;
  const orgId = state.selectedOrg?.id;
  if (!userId || !orgId) return null;
  return { userId, orgId };
}

function multiAgentSessionStorageKey(userId: string, orgId: string): string {
  return `ma:session:${userId}:${orgId}`;
}

function readPersisted(): PersistedMultiAgentSession {
  const tenant = resolveMultiAgentTenant();
  if (!tenant) return { runId: null, sessionId: null };
  try {
    const key = multiAgentSessionStorageKey(tenant.userId, tenant.orgId);
    const raw = localStorage.getItem(key);
    if (!raw) return { runId: null, sessionId: null };
    const v = JSON.parse(raw);
    return {
      runId: typeof v.runId === "string" ? v.runId : null,
      sessionId: typeof v.sessionId === "string" ? v.sessionId : null,
    };
  } catch {
    return { runId: null, sessionId: null };
  }
}

export function readPersistedMultiAgentSession(): PersistedMultiAgentSession {
  return readPersisted();
}

function writePersisted(p: Partial<PersistedMultiAgentSession>): void {
  const tenant = resolveMultiAgentTenant();
  if (!tenant) return;
  try {
    const key = multiAgentSessionStorageKey(tenant.userId, tenant.orgId);
    const prev = readPersisted();
    localStorage.setItem(key, JSON.stringify({ ...prev, ...p }));
    localStorage.removeItem(LEGACY_STORAGE_KEY);
  } catch { /* quota / SSR safety */ }
}

function clearPersisted(): void {
  try {
    localStorage.removeItem(LEGACY_STORAGE_KEY);
    const tenant = resolveMultiAgentTenant();
    if (tenant) {
      localStorage.removeItem(multiAgentSessionStorageKey(tenant.userId, tenant.orgId));
    }
  } catch { /* noop */ }
}

// ── Sent-attachment thumbnail persistence (survive a page refresh) ───────────
// The chat/thread rehydrate from the backend on reload, which carries NO
// thumbnails — so a sent image/screenshot preview vanished on refresh. We keep a
// small, tenant-scoped map { runId → [downscaled thumbnails] } in localStorage and
// re-attach it during hydrateFromDetail. Thumbnails are downscaled (full
// screenshots would blow the ~5MB localStorage budget) and the map is bounded.
const _MA_ATTACHMENTS_MAX_RUNS = 15;

function multiAgentAttachmentsStorageKey(userId: string, orgId: string): string {
  return `ma:attachments:${userId}:${orgId}`;
}

/** Downscale an image data URL to a tiny thumbnail for durable storage. Returns
 *  the input unchanged for non-images or on any failure (best-effort). */
async function _downscaleDataUrl(dataUrl: string, maxPx = 200): Promise<string> {
  try {
    if (!dataUrl.startsWith("data:image/")) return dataUrl;
    const img = new Image();
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = dataUrl;
    });
    const longest = Math.max(img.width, img.height) || 1;
    const scale = Math.min(1, maxPx / longest);
    const w = Math.max(1, Math.round(img.width * scale));
    const h = Math.max(1, Math.round(img.height * scale));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const cx = canvas.getContext("2d");
    if (!cx) return dataUrl;
    cx.drawImage(img, 0, 0, w, h);
    return canvas.toDataURL("image/jpeg", 0.6);
  } catch {
    return dataUrl;
  }
}

/** Persist a run's sent attachments (downscaled) so their bubble thumbnails
 *  survive a refresh. Fire-and-forget; failures are non-fatal.
 *
 *  Two-phase write:
 *  1. Synchronously write attachment metadata (id/name/kind, no thumbnail bytes)
 *     so a page refresh that happens before the canvas downscale finishes still
 *     shows attachment chips on reload.
 *  2. Async: downscale thumbnails, then overwrite with the richer record.
 */
export async function persistRunAttachments(
  runId: string,
  attachments: ChatMsgAttachment[],
): Promise<void> {
  const tenant = resolveMultiAgentTenant();
  if (!tenant || !runId || !attachments || attachments.length === 0) return;

  const writeToStorage = (entries: ChatMsgAttachment[]) => {
    try {
      const key = multiAgentAttachmentsStorageKey(tenant.userId, tenant.orgId);
      const raw = localStorage.getItem(key);
      const map: Record<string, ChatMsgAttachment[]> = raw ? JSON.parse(raw) : {};
      map[runId] = entries;
      const ids = Object.keys(map);
      if (ids.length > _MA_ATTACHMENTS_MAX_RUNS) {
        for (const old of ids.slice(0, ids.length - _MA_ATTACHMENTS_MAX_RUNS)) delete map[old];
      }
      localStorage.setItem(key, JSON.stringify(map));
    } catch { /* quota / SSR / parse error — non-fatal */ }
  };

  // Phase 1 — synchronous: write metadata only (no large thumbnail bytes).
  // This runs before any await, so a refresh in the next millisecond still
  // finds the attachment record and shows chips (without image previews).
  writeToStorage(attachments.map((a) => ({ id: a.id, name: a.name, kind: a.kind })));

  // Phase 2 — async: downscale thumbnails for durable image previews and
  // overwrite the metadata-only record with the richer version.
  try {
    const slim: ChatMsgAttachment[] = await Promise.all(
      attachments.map(async (a) => ({
        id: a.id,
        name: a.name,
        kind: a.kind,
        thumbnailDataUrl: a.thumbnailDataUrl
          ? await _downscaleDataUrl(a.thumbnailDataUrl)
          : undefined,
      })),
    );
    writeToStorage(slim);
  } catch {
    /* Phase 1 metadata record stays in localStorage — acceptable fallback */
  }
}

/** Read a run's persisted attachments (for hydrateFromDetail). */
function readRunAttachments(runId: string): ChatMsgAttachment[] | undefined {
  const tenant = resolveMultiAgentTenant();
  if (!tenant || !runId) return undefined;
  try {
    const raw = localStorage.getItem(
      multiAgentAttachmentsStorageKey(tenant.userId, tenant.orgId),
    );
    if (!raw) return undefined;
    const map = JSON.parse(raw) as Record<string, ChatMsgAttachment[]>;
    const a = map[runId];
    return Array.isArray(a) && a.length > 0 ? a : undefined;
  } catch {
    return undefined;
  }
}
// Session-first URL model: the URL carries ONLY ?session=<id> (the durable key);
// run ids stay internal to the store. A refresh restores the session and resolves
// its latest run. We also strip any stale ?run= so the two params can never
// disagree (?run= remains a read-only back-compat deep link on first load only).
// No-op on SSR.
function pushSessionToUrl(_sessionId: string | null): void {
  // Keep the URL CLEAN — dev-agent parity. dev-agent never exposes a run OR
  // session id in the address bar; the session is restored from localStorage
  // (writePersisted) on refresh, and ?run=/?session= stay read-only deep-links
  // consumed once on first load. So we only ever STRIP id params here, never
  // write them. (Param kept for call-site compatibility.)
  try {
    const url = new URL(window.location.href);
    let changed = false;
    if (url.searchParams.has("run")) { url.searchParams.delete("run"); changed = true; }
    if (url.searchParams.has("session")) { url.searchParams.delete("session"); changed = true; }
    if (changed) window.history.replaceState({}, "", url.toString());
  } catch { /* noop */ }
}

export const useMultiAgentStore = create<MultiAgentState>((set, get) => ({
  ..._initial,
  history: [],
  thread: [],
  sessionId: null,

  startRun: (runId, intent, attachments) => {
    // The run id NEVER goes in the URL. We still key the in-memory session +
    // localStorage off runId when no real session exists yet (so a refresh can
    // restore from persisted state), but the URL only ever carries a real
    // session id distinct from the run — never the run id (a brand-new run
    // keeps a clean URL).
    const sid = get().sessionId || runId;
    pushSessionToUrl(sid === runId ? null : sid);
    writePersisted({ runId, sessionId: sid });
    set((s) => ({
      ..._initial,
      history: s.history,
      thread: s.thread,
      sessionId: sid,
      // PERSIST across the run transition so the right panel / Workspace don't
      // flash empty during a new run's initiation (same intent as reset(),
      // which also keeps these). Run-specific state (nodes, cost, verifier,
      // timeline, status) still resets — the new run repopulates it. Artifacts,
      // session context and loaded memory carry over until new data replaces them.
      artifacts: s.artifacts,
      sessionContext: s.sessionContext,
      memoryLoaded: s.memoryLoaded,
      runId,
      status: "starting",
      runStartedAt: Date.now(),
      runFinishedAt: null,
      // When attachments aren't passed (hydration path — page refresh of a
      // running run), restore from the localStorage record written by
      // persistRunAttachments so the bubble thumbnails survive the refresh.
      // An explicit [] from a no-attachment send is preserved as-is.
      chat: [{ id: crypto.randomUUID(), role: "user", text: intent, attachments: attachments ?? readRunAttachments(runId) }],
    }));
  },

  setStatus: (status) => set({ status }),
  setLane: (lane) => set({ lane }),
  setBlockedAction: (blockedAction) => set({ blockedAction }),
  setSfConnected: (sfConnected) => set({ sfConnected }),

  appendUserMessage: (text) =>
    set((s) => ({
      chat: [...s.chat, { id: crypto.randomUUID(), role: "user", text }],
    })),

  appendReasoning: (nodeId, text) =>
    set((s) => ({
      reasoningBuffer: {
        ...s.reasoningBuffer,
        [nodeId]: (s.reasoningBuffer[nodeId] || "") + text,
      },
    })),

  endReasoning: (nodeId) =>
    set((s) => {
      const reasoning = s.reasoningBuffer[nodeId] || "";
      if (!reasoning.trim()) return {};
      const { [nodeId]: _, ...rest } = s.reasoningBuffer;
      return {
        reasoningBuffer: rest,
        chat: [
          ...s.chat,
          {
            id: crypto.randomUUID(),
            role: "assistant",
            text: "",
            reasoning,
          },
        ],
      };
    }),

  appendText: (nodeId, text) =>
    set((s) => ({
      textBuffer: { ...s.textBuffer, [nodeId]: (s.textBuffer[nodeId] || "") + text },
      finalText: nodeId === "synthesizer" ? s.finalText + text : s.finalText,
    })),

  endText: (nodeId) =>
    set((s) => {
      const text = s.textBuffer[nodeId] || "";
      if (!text.trim()) return {};
      const { [nodeId]: _, ...rest } = s.textBuffer;
      if (nodeId !== "synthesizer") {
        return {
          textBuffer: rest,
        };
      }
      return {
        textBuffer: rest,
        chat: [
          ...s.chat,
          { id: crypto.randomUUID(), role: "assistant", text },
        ],
        // synthesizer's terminal text IS the final report
        finalMarkdown: sanitizeArtifactMarkdown(text),
      };
    }),

  setPlan: (nodes, edges, todo) => {
    const nodesMap: Record<string, AgentNodeData> = {};
    for (const n of nodes) nodesMap[n.id] = n;
    // plan_gate now auto-approves; we go straight from planning to running.
    // Keep status as planning here — `plan_approved` SSE flips it to running.
    set({ nodes: nodesMap, edges, todo, status: "planning" });
  },

  setNodeStatus: (nodeId, status, toolInFlight) =>
    set((s) => {
      const prev = s.nodes[nodeId];
      if (!prev) return {};
      return {
        nodes: {
          ...s.nodes,
          [nodeId]: { ...prev, status, toolInFlight },
        },
        todo: s.todo.map((t) =>
          t.id === nodeId
            ? {
                ...t,
                status:
                  status === "done"
                    ? "done"
                    : status === "failed"
                      ? "failed"
                      : status === "running" || status === "tool_calling"
                        ? "running"
                        : t.status,
              }
            : t,
        ),
      };
    }),

  setNodeOutput: (nodeId, output) =>
    set((s) => {
      const prev = s.nodes[nodeId];
      if (!prev) return {};
      return { nodes: { ...s.nodes, [nodeId]: { ...prev, output } } };
    }),

  activateEdge: (from, to) =>
    set((s) => ({
      edges: s.edges.map((e) =>
        e.from === from && e.to === to ? { ...e, active: true } : e,
      ),
    })),

  approvePlan: () => set({ status: "running" }),

  addPartialFinding: (nodeId, role, summary) =>
    set((s) => ({
      partialFindings: [...s.partialFindings, { nodeId, role, summary }],
    })),

  setMemoryLoaded: (m) => set({ memoryLoaded: m }),
  setSessionContext: (m) => set({ sessionContext: m }),
  setPromotionTotals: (t) => set({ promotionTotals: t }),
  setCost: (c) => set({ cost: c }),
  setFinalArtifact: (artifactId) => set({ finalArtifactId: artifactId }),
  setFollowUps: (f) => set({ followUps: Array.isArray(f) ? f : [] }),
  setHistory: (h) => set({ history: h }),
  setFeedback: (rating) => set({ feedbackRating: rating }),
  setProposedFixes: (fixes) => set({ proposedFixes: fixes }),
  setVerifier: (v) => set({ verifier: v }),
  setContractViolation: (v) => set({ contractViolation: v }),
  setAutoFixFailed: (v) => set({ autoFixFailed: v }),
  // Mirror sessionId + runId to tenant-scoped localStorage.
  _persistMA: () => {
    const s = get();
    writePersisted({
      runId: s.runId,
      sessionId: s.sessionId,
    });
  },

  pushTurnToThread: () => set((s) => {
    const userMsg = s.chat.find((m) => m.role === "user");
    const intent = userMsg?.text || "";
    const attachments = userMsg?.attachments;
    const md = sanitizeArtifactMarkdown(s.finalMarkdown).trim();
    // Capture the activity timeline (last 50 entries) and reasoning
    // tail so prior turns are visually rich, not just an answer.
    const activity = s.activityLog.slice(-50).map((e) => ({
      id: e.id, kind: e.kind, text: e.text, tone: e.tone,
      tool: e.tool, argsSummary: e.argsSummary, durationMs: e.durationMs,
    }));
    const reasoning = Object.values(s.reasoningBuffer)
      .filter(Boolean).join("\n\n").trim().slice(-1500);
    // Only THIS run's artifacts (tagged by runId) — not the whole accumulated
    // set — so the snapshotted turn renders its own diff/code/evidence, not the
    // entire session's artifacts piling onto every turn.
    const artifactIds = s.artifacts
      .filter((a) => (a.runId ? a.runId === s.runId : true))
      .map((a) => a.id);
    // Preserve the turn if it produced ANY output — answer prose, artifacts
    // (code/diffs), or an activity timeline. Previously this bailed whenever the
    // answer markdown was empty, which made artifact-only turns (e.g. a refactor
    // that produced a code diff but no prose) DISAPPEAR on the next send.
    if (!s.runId || !intent || (!md && artifactIds.length === 0 && activity.length === 0)) return s;
    const nextTurn = {
      runId: s.runId,
      intent, attachments, finalMarkdown: md, lane: s.lane,
      activity, reasoning, artifactIds,
    };
    // Keep chronological order: if this run is ALREADY in the thread (e.g.
    // restored mid-conversation), update it in place rather than dropping it
    // and re-appending — re-appending would move a middle turn to the end and
    // render the follow-up out of order. Only a genuinely new turn appends.
    const existingIdx = s.thread.findIndex((turn) => turn.runId === s.runId);
    if (existingIdx >= 0) {
      const thread = s.thread.slice();
      thread[existingIdx] = nextTurn;
      return { thread };
    }
    return { thread: [...s.thread, nextTurn] };
  }),
  clearThread: () => {
    clearPersisted();
    pushSessionToUrl(null);
    // FULL blank-session reset (keep only the run-list history). Previously this
    // cleared just thread+sessionId, so the last answer (finalMarkdown), its
    // artifacts, diffs and verdicts lingered on screen after "New session" — it
    // looked like the current session until the next send finally wiped them.
    set({ ..._initial, history: get().history });
  },
  setPlannerFallback: (f) => set({ plannerFallback: f }),
  setPlanReused: (p) => set({ planReused: p }),
  selectNode: (nodeId) => set({ selectedNodeId: nodeId }),

  appendActivity: (entry) =>
    set((s) => {
      // Cap the log at 500 entries so very long runs don't blow memory.
      const next = s.activityLog.length >= 500
        ? [...s.activityLog.slice(-499), entry]
        : [...s.activityLog, entry];
      return { activityLog: next };
    }),
  setSseStatus: (s) => set({ sseStatus: s }),
  addArtifact: (a) =>
    set((st) => ({
      artifacts: st.artifacts.some((x) => x.id === a.id)
        ? st.artifacts
        // Tag the artifact with the run that produced it (the live run) so each
        // turn renders its OWN artifacts. Falls back to a.runId if the caller set it.
        : [...st.artifacts, { ...a, runId: a.runId ?? st.runId ?? undefined, name: sanitizeArtifactLabel(a.name, a.id.slice(0, 8)) }],
    })),
  removeArtifact: (id) =>
    set((st) => ({ artifacts: st.artifacts.filter((a) => a.id !== id) })),

  // Called on normal terminal events (run_completed / run_failed). Same
  // shape as markCancelled but keeps the run "done"/"failed" rather than
  // "cancelled", and doesn't downgrade the sseStatus to "disconnected"
  // — that pill is hidden once the run is terminal anyway.
  finalizeRun: (s) =>
    set((st) => {
      const nodes: Record<string, AgentNodeData> = {};
      for (const [id, n] of Object.entries(st.nodes)) {
        const wasInFlight = n.status === "running" || n.status === "tool_calling";
        nodes[id] = {
          ...n,
          // Successful end → roll forward to "done"; failed → mark "failed"
          status: wasInFlight ? (s === "done" ? "done" : "failed") : n.status,
          toolInFlight: undefined,
        };
      }
      return { status: s, nodes, runFinishedAt: st.runFinishedAt ?? Date.now() };
    }),

  // Optimistic stop: invoked when the user clicks Stop OR the run_cancelled
  // SSE event arrives. Clears every in-flight indicator so the UI freezes
  // immediately rather than waiting for in-flight tool calls to finish.
  markCancelled: () =>
    set((st) => {
      const nodes: Record<string, AgentNodeData> = {};
      for (const [id, n] of Object.entries(st.nodes)) {
        const wasInFlight = n.status === "running" || n.status === "tool_calling";
        nodes[id] = {
          ...n,
          status: wasInFlight ? "skipped" : n.status,
          toolInFlight: undefined,
        };
      }
      return {
        status: "cancelled",
        nodes,
        sseStatus: "disconnected",
        runFinishedAt: st.runFinishedAt ?? Date.now(),
      };
    }),

  hydrateFromDetail: (d, options) => {
    const nodesMap: Record<string, AgentNodeData> = {};
    for (const n of d.nodes) {
      nodesMap[n.id] = {
        id: n.id,
        role: n.role,
        task: n.task,
        status: n.status as NodeStatus,
        isSupervisor: n.isSupervisor,
        toolInFlight: n.toolInFlight ?? undefined,
      };
    }
    const edges: PlanEdge[] = (d.edges || []).map(([from, to]) => ({ from, to }));
    // Past-run hydrate: keep only the user's original intent in chat.
    // The synth/worker text buffers come back as "assistant" messages from
    // the /detail endpoint but they're already in finalMarkdown — rendering
    // them as chat bubbles in the past-run view duplicates content and
    // confuses the user-asked / agent-answer header in ChatPanel.
    // Re-attach the ACTIVE run's sent thumbnails to its user bubble (the first
    // user message), so an image/screenshot preview survives a refresh.
    const activeRunAttachments = readRunAttachments(d.run.id);
    const chat: ChatMessage[] = d.chat
      .filter((m) => m.role === "user")
      .map((m, i) => ({
        id: crypto.randomUUID(),
        role: m.role,
        text: cleanDisplayIntent(m.text || ""),
        reasoning: m.reasoning,
        attachments: i === 0 ? activeRunAttachments : undefined,
      }));
    const todo: TodoItem[] = d.todo.map((t) => ({
      id: t.id,
      label: t.label,
      status: t.status as TodoItem["status"],
    }));
    const restoredThread = (d.session_runs || [])
      .filter((r) => r.runId !== d.run.id && r.intent && r.finalMarkdown)
      // Render in real conversation order — session_runs arrives in no
      // guaranteed order, so sort by start time (oldest first). Without this
      // a refreshed/rehydrated session can show follow-ups above the turn
      // they came after.
      .slice()
      .sort((a, b) => (a.startedAt || "").localeCompare(b.startedAt || ""))
      .map((r) => ({
        runId: r.runId,
        intent: cleanDisplayIntent(r.intent),
        // Re-attach this past turn's sent thumbnails from localStorage — the
        // backend session_runs carry no attachments, so without this the image/
        // screenshot previews vanish on refresh.
        attachments: readRunAttachments(r.runId),
        finalMarkdown: sanitizeArtifactMarkdown(r.finalMarkdown),
        lane: r.lane,
        activity: [],
        reasoning: "",
        // Restore this past run's artifact ids (from the backend session_runs)
        // so its diff/code/citation cards re-render on reload instead of
        // collapsing to plain text.
        artifactIds: (r.artifacts ?? []).map((a) => a.id),
      }));
    const isTerminal = ["done", "failed", "cancelled"].includes(d.run.status);
    set((s) => ({
      ..._initial,
      // Preserve cross-run conversation state for follow-up navigation.
      // If a refresh left localStorage stale/empty, backfill from the
      // durable session_runs returned by the backend. When local turns
      // exist, keep their richer activity/reasoning snapshots.
      history: s.history,
      thread: options?.preserveSession
        ? (() => {
            const localByRun = new Map(s.thread.map((turn) => [turn.runId, turn]));
            const restoredIds = new Set(restoredThread.map((turn) => turn.runId));
            return [
              ...restoredThread.map((turn) => localByRun.get(turn.runId) || turn),
              ...s.thread.filter((turn) => !restoredIds.has(turn.runId)),
            ];
          })()
        : restoredThread,
      sessionId: options?.preserveSession
        ? s.sessionId
        : d.run.session_id || d.session_id || s.sessionId || d.run.id,
      runId: d.run.id,
      // Restore run timing from the durable detail so the elapsed timer shows
      // the real run duration for a loaded/past run (and keeps counting if it's
      // still in flight).
      runStartedAt: d.run.started_at ? Date.parse(d.run.started_at) : null,
      runFinishedAt: d.run.finished_at ? Date.parse(d.run.finished_at) : null,
      // Chat restores are resumable sessions by default. The separate
      // runs page is the read-only detail surface; only callers that
      // explicitly ask for viewOnly should hide the composer.
      readOnly: Boolean(options?.viewOnly && isTerminal),
      status: d.run.status === "done" ? "done" : d.run.status === "failed" ? "failed" : (d.run.status === "cancelled" ? "cancelled" : "running"),
      lane: d.run.lane,
      nodes: nodesMap,
      edges,
      chat,
      todo,
      partialFindings: d.partial_findings,
      proposedFixes: d.proposed_fixes,
      // Don't let a terminal /detail with an EMPTY final answer wipe the answer
      // we already streamed/loaded for THIS run — the just-completed detail can
      // momentarily lack final_markdown (e.g. synthesis persisted as an artifact
      // before the run row's final text is written), which made the code +
      // contract "disappear" a couple minutes in. Switching to a DIFFERENT run
      // uses that run's detail as-is.
      finalMarkdown:
        sanitizeArtifactMarkdown(d.final_markdown) ||
        (d.run.id === s.runId ? s.finalMarkdown : ""),
      finalArtifactId: d.final_artifact_id || (d.run.id === s.runId ? s.finalArtifactId : null),
      followUps: Array.isArray(d.follow_ups) ? d.follow_ups : [],
      cost: d.cost,
      memoryLoaded: d.memory_loaded,
      sessionContext: d.session_context,
      promotionTotals: d.promotion_totals,
      // Restore the run's artifacts so `[label](artifact://id)` citations in
      // finalMarkdown resolve to chips. Without this, hydrated/past runs have
      // an empty artifact set and every citation renders as a broken link.
      artifacts: (() => {
        const mapArtifact = (a: {
          id: string; type: string; name: string; tool_name: string;
          metadata: Record<string, unknown> | null; created_at: string | null;
        }, runId: string) => {
          const meta = (a.metadata ?? {}) as Record<string, unknown>;
          const size = meta.byte_size ?? meta.size_bytes ?? meta.bytes;
          // Typed intake metadata lives under metadata.dependencies.intake (DB)
          // or metadata.intake (SSE). Present only for uploaded attachments.
          const deps = (meta.dependencies ?? {}) as Record<string, unknown>;
          const intake = (meta.intake ?? deps.intake ?? null) as Record<string, unknown> | null;
          return {
            id: a.id,
            type: a.type,
            name: sanitizeArtifactLabel(a.name, a.id.slice(0, 8)),
            toolName: a.tool_name,
            byteSize: typeof size === "number" ? size : 0,
            createdAt: a.created_at ? Date.parse(a.created_at) : 0,
            runId,
            intake,
          };
        };
        const incoming = (d.artifacts ?? []).map((a) => mapArtifact(a, d.run.id));
        // ALSO include every PAST run's artifacts (backend now returns them per
        // session_run), TAGGED with that run's id so each turn renders its OWN
        // artifacts instead of the whole session's set piling up at the end.
        const pastArtifacts = (d.session_runs ?? []).flatMap((r) => (r.artifacts ?? []).map((a) => mapArtifact(a, r.runId)));
        // NEVER lose artifacts: union by id (current run wins on conflict).
        if (options?.viewOnly) {
          const v = new Map(pastArtifacts.map((a) => [a.id, a]));
          for (const a of incoming) v.set(a.id, a);
          return Array.from(v.values());
        }
        const byId = new Map(get().artifacts.map((a) => [a.id, a]));
        for (const a of pastArtifacts) byId.set(a.id, a);
        for (const a of incoming) byId.set(a.id, a);
        return Array.from(byId.values());
      })(),
    }));
  },

  // reset() keeps history (drawer cache) AND the conversation thread
  // AND the sessionId — refine() calls reset() between turns and we
  // want previous Q&As + the session to stay coherent. Use clearThread()
  // for a true new chat (also clears sessionId).
  reset: () => set({
    ..._initial,
    history: get().history,
    thread: get().thread,
    sessionId: get().sessionId,
    // PERSIST artifacts across runs (code, diffs, exports). reset() runs before
    // every new send; wiping artifacts here is what made the diffs disappear and
    // the Workspace empty between turns. They now persist until the user clears
    // a panel or switches tenant/session (resetMultiAgentForTenantChange →
    // clearThread also drops them). Prior-turn diffs resolve by artifact id.
    artifacts: get().artifacts,
    // PRESERVE the just-finished run's answer + verdicts. reset() is called
    // BEFORE the async /detail hydrate (MultiAgentRoute) and before startRun on
    // a follow-up; wiping finalMarkdown here is what made a shown answer (and its
    // code/contract) DISAPPEAR during that window. startRun() spreads _initial
    // and does NOT re-add these, so the NEW run still starts clean/streaming.
    finalMarkdown: get().finalMarkdown,
    finalArtifactId: get().finalArtifactId,
    proposedFixes: get().proposedFixes,
    contractViolation: get().contractViolation,
    verifier: get().verifier,
    autoFixFailed: get().autoFixFailed,
  }),
}));

/** Clear persisted session + in-memory run state (org switch, logout, auth failure).
 *  Also drops `history` (the prior tenant's run list) so another user's runs can
 *  never flash on screen before the new tenant's query refetches. */
export function resetMultiAgentForTenantChange(): void {
  const { clearThread, reset, setHistory } = useMultiAgentStore.getState();
  clearThread();
  reset();
  setHistory([]);
}

// Auto-persist whenever the conversation state changes. Subscribing
// once at module load keeps every setter (pushTurnToThread, startRun,
// pushTurnRaw refine flows, etc.) honest without us threading
// writePersisted() through each one.
if (typeof window !== "undefined") {
  let lastKey = "";
  useMultiAgentStore.subscribe((s) => {
    const key = `${s.runId}|${s.sessionId}`;
    if (key === lastKey) return;
    lastKey = key;
    writePersisted({
      runId: s.runId,
      sessionId: s.sessionId,
    });
    // Session-first: reflect ONLY a durable session id in the URL — never the
    // run id. When sessionId is just the runId fallback (no real session yet),
    // keep the URL clean; restore still works via persisted runId in localStorage.
    pushSessionToUrl(s.sessionId && s.sessionId !== s.runId ? s.sessionId : null);
  });
}

// ── Organization scoping ─────────────────────────────────────────────────────
//
// Registered here, at module scope, rather than in a `useEffect` on the Weave
// page. The effect only ran while that page was mounted, so switching orgs from
// anywhere else left this store holding the previous organization's session,
// thread and run history — which then surfaced the next time Weave was opened.
registerOrgScopedReset('multiAgentStore', resetMultiAgentForTenantChange);
